import React, { useState, useEffect, useRef } from 'react';
import { X, Upload, FileText, Code, CheckCircle, XCircle, Loader, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import securityApi from '../../../api/securityApi';
import TaskStatusBadge from './TaskStatusBadge';
import TaskPriorityBadge from './TaskPriorityBadge';
import EvidenceItem from './EvidenceItem';
import StructuredEvidenceForm from './StructuredEvidenceForm';

const EVIDENCE_TYPES = [
  { value: 'document', label: 'Document' },
  { value: 'screenshot', label: 'Screenshot' },
  { value: 'config_snapshot', label: 'Config Snapshot' },
  { value: 'log', label: 'Log' },
  { value: 'api_response', label: 'API Response' },
];

const getErrorMessage = (err, fallback) => {
  const detail = err.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((d) => d.msg || JSON.stringify(d)).join('; ');
  return fallback;
};

const TaskDetailPanel = ({ task, projectId, onClose, onTaskUpdated }) => {
  const [taskDetail, setTaskDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('file');
  const [uploading, setUploading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState('');

  // File upload state
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileType, setFileType] = useState('document');
  const [fileTitle, setFileTitle] = useState('');
  const fileInputRef = useRef(null);

  // Text evidence state
  const [textType, setTextType] = useState('document');
  const [textTitle, setTextTitle] = useState('');
  const [textContent, setTextContent] = useState('');

  // N/A state
  const [showNAForm, setShowNAForm] = useState(false);
  const [justification, setJustification] = useState('');

  // Structured evidence state
  const [structuredSchema, setStructuredSchema] = useState(null);
  const [structuredSubmission, setStructuredSubmission] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [structuredSubmitting, setStructuredSubmitting] = useState(false);

  const fetchTaskDetail = async () => {
    try {
      setLoading(true);
      setStructuredSchema(null);
      setStructuredSubmission(null);
      setFieldErrors({});
      const data = await securityApi.getTaskDetail(projectId, task.id);
      setTaskDetail(data);
      if (data.requires_structured_evidence) {
        try {
          const se = await securityApi.getStructuredEvidence(projectId, task.id);
          setStructuredSchema(se.schema || null);
          setStructuredSubmission(se.submission || {});
        } catch (err) {
          console.error('Failed to fetch structured evidence:', err);
        }
      }
    } catch (err) {
      console.error('Failed to fetch task detail:', err);
      toast.error('Failed to load task details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTaskDetail();
  }, [task.id, projectId]);

  const handleFileUpload = async (e) => {
    e.preventDefault();
    if (!selectedFile) {
      setError('Please select a file');
      return;
    }

    const maxSize = 10 * 1024 * 1024;
    if (selectedFile.size > maxSize) {
      setError('File size exceeds 10MB limit');
      return;
    }

    const allowedTypes = ['.png', '.jpg', '.jpeg', '.pdf'];
    const ext = '.' + selectedFile.name.split('.').pop().toLowerCase();
    if (!allowedTypes.includes(ext)) {
      setError('Only PNG, JPG, and PDF files are allowed');
      return;
    }

    setUploading(true);
    setError('');
    try {
      await securityApi.uploadFileEvidence(
        projectId,
        task.id,
        selectedFile,
        fileType,
        fileTitle || selectedFile.name
      );
      toast.success('Evidence uploaded');
      setSelectedFile(null);
      setFileTitle('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      await fetchTaskDetail();
      onTaskUpdated();
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to upload evidence'));
    } finally {
      setUploading(false);
    }
  };

  const handleTextSubmit = async (e) => {
    e.preventDefault();
    if (!textContent.trim()) {
      setError('Content is required');
      return;
    }

    setUploading(true);
    setError('');
    try {
      await securityApi.createTextEvidence(projectId, task.id, {
        type: textType,
        title: textTitle || 'Text Evidence',
        content: textContent.trim(),
      });
      toast.success('Evidence added');
      setTextTitle('');
      setTextContent('');
      await fetchTaskDetail();
      onTaskUpdated();
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to add evidence'));
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteEvidence = async (evidenceId) => {
    try {
      await securityApi.deleteEvidence(projectId, task.id, evidenceId);
      toast.success('Evidence deleted');
      await fetchTaskDetail();
      onTaskUpdated();
    } catch (err) {
      toast.error('Failed to delete evidence');
      throw err;
    }
  };

  const handleMarkComplete = async () => {
    setUpdating(true);
    setError('');
    try {
      await securityApi.updateProjectTask(projectId, task.id, {
        status: 'manually_verified',
      });
      toast.success('Task marked as complete');
      await fetchTaskDetail();
      onTaskUpdated();
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to update task'));
    } finally {
      setUpdating(false);
    }
  };

  const handleMarkNA = async () => {
    if (!justification.trim()) {
      setError('Justification is required');
      return;
    }

    setUpdating(true);
    setError('');
    try {
      await securityApi.updateProjectTask(projectId, task.id, {
        status: 'not_applicable',
        justification: justification.trim(),
      });
      toast.success('Task marked as not applicable');
      await fetchTaskDetail();
      onTaskUpdated();
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to update task'));
    } finally {
      setUpdating(false);
      setShowNAForm(false);
    }
  };

  const handleSaveDraft = async (fieldValues) => {
    try {
      await securityApi.submitStructuredEvidence(projectId, task.id, fieldValues, true);
      toast.success('Draft saved');
      setFieldErrors({});
      setStructuredSubmission(fieldValues);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to save draft'));
    }
  };

  const handleSubmitStructuredEvidence = async (fieldValues) => {
    setStructuredSubmitting(true);
    setFieldErrors({});
    try {
      // Single atomic call: validates, saves evidence, and marks task as manually_verified
      await securityApi.submitStructuredEvidence(projectId, task.id, fieldValues, false);
      toast.success('Evidence submitted and task marked complete');
      setStructuredSubmission(fieldValues);
      await fetchTaskDetail();
      onTaskUpdated();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.field_errors) {
        setFieldErrors(detail.field_errors);
        toast.error('Please fix the highlighted fields');
      } else {
        toast.error(getErrorMessage(err, 'Failed to submit evidence'));
      }
    } finally {
      setStructuredSubmitting(false);
    }
  };

  const handleStructuredFileUpload = async (fieldKey, file, fieldLabel) => {
    // Upload file as regular evidence, return the evidence ID as the field value
    const result = await securityApi.uploadFileEvidence(
      projectId,
      task.id,
      file,
      'document',
      fieldLabel || fieldKey
    );
    // The API returns the created evidence record; extract its ID
    return result.id;
  };

  const evidenceCount = taskDetail?.evidence?.length || 0;
  const isCompleted = taskDetail?.status === 'auto_verified' ||
                      taskDetail?.status === 'manually_verified' ||
                      taskDetail?.status === 'not_applicable';

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-30 z-40" onClick={onClose} />

      {/* Panel */}
      <div className="fixed inset-y-0 right-0 w-full max-w-lg bg-white shadow-2xl z-50 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-start justify-between p-4 border-b border-gray-200 bg-gray-50">
          <div className="flex-1 min-w-0 pr-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs font-mono bg-gray-200 text-gray-700 px-2 py-0.5 rounded">
                {task.control_id}
              </span>
              <TaskPriorityBadge priority={task.priority} />
              <TaskStatusBadge status={taskDetail?.status || task.status} />
            </div>
            <h2 className="text-lg font-semibold text-gray-900 leading-tight">
              {taskDetail?.title || task.title}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="flex-shrink-0 p-1 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center p-8">
              <Loader className="w-6 h-6 animate-spin text-teal-500" />
            </div>
          ) : (
            <div className="p-4 space-y-6">
              {/* Task Info */}
              {taskDetail?.description && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-1">Description</h3>
                  <p className="text-sm text-gray-600">{taskDetail.description}</p>
                </div>
              )}

              {taskDetail?.evidence_required && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-1">Evidence Required</h3>
                  <p className="text-sm text-gray-600 bg-amber-50 border border-amber-200 rounded p-2">
                    {taskDetail.evidence_required}
                  </p>
                </div>
              )}

              {taskDetail?.integration_source && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-1">Integration</h3>
                  <p className="text-sm text-gray-600">
                    Verifiable via <span className="font-medium">{taskDetail.integration_source}</span>
                  </p>
                </div>
              )}

              {/* Evidence Section */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-2">
                  Evidence ({evidenceCount})
                </h3>
                {evidenceCount === 0 ? (
                  <p className="text-sm text-gray-500 italic">No evidence collected yet</p>
                ) : (
                  <div className="space-y-2">
                    {taskDetail.evidence.map((ev) => (
                      <EvidenceItem
                        key={ev.id}
                        evidence={ev}
                        projectId={projectId}
                        taskId={task.id}
                        onDelete={handleDeleteEvidence}
                        canDelete={!isCompleted}
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* Add Evidence Section - only for non-completed tasks */}
              {!isCompleted && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">
                    {structuredSchema ? 'Evidence Form' : 'Add Evidence'}
                  </h3>

                  {structuredSchema ? (
                    <StructuredEvidenceForm
                      schema={structuredSchema}
                      initialValues={structuredSubmission || {}}
                      fieldErrors={fieldErrors}
                      onSaveDraft={handleSaveDraft}
                      onSubmit={handleSubmitStructuredEvidence}
                      onFileUpload={handleStructuredFileUpload}
                      submitting={structuredSubmitting}
                    />
                  ) : (
                    <>
                      {/* Tab Toggle */}
                      <div className="flex border-b border-gray-200 mb-3">
                        <button
                          onClick={() => { setActiveTab('file'); setError(''); }}
                          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                            activeTab === 'file'
                              ? 'border-teal-500 text-teal-600'
                              : 'border-transparent text-gray-500 hover:text-gray-700'
                          }`}
                        >
                          <Upload className="w-4 h-4 inline mr-1" />
                          File Upload
                        </button>
                        <button
                          onClick={() => { setActiveTab('text'); setError(''); }}
                          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                            activeTab === 'text'
                              ? 'border-teal-500 text-teal-600'
                              : 'border-transparent text-gray-500 hover:text-gray-700'
                          }`}
                        >
                          <Code className="w-4 h-4 inline mr-1" />
                          Text Evidence
                        </button>
                      </div>

                      {/* File Upload Tab */}
                      {activeTab === 'file' && (
                        <form onSubmit={handleFileUpload} className="space-y-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">File</label>
                            <input
                              ref={fileInputRef}
                              type="file"
                              accept=".png,.jpg,.jpeg,.pdf"
                              onChange={(e) => setSelectedFile(e.target.files[0] || null)}
                              className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-medium file:bg-teal-50 file:text-teal-700 hover:file:bg-teal-100"
                            />
                            <p className="mt-1 text-xs text-gray-500">PNG, JPG, or PDF. Max 10MB.</p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                            <select
                              value={fileType}
                              onChange={(e) => setFileType(e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                            >
                              <option value="screenshot">Screenshot</option>
                              <option value="document">Document</option>
                              <option value="config_snapshot">Config Snapshot</option>
                              <option value="log">Log</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Title (optional)</label>
                            <input
                              type="text"
                              value={fileTitle}
                              onChange={(e) => setFileTitle(e.target.value)}
                              placeholder="Brief description of this evidence"
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                            />
                          </div>
                          <button
                            type="submit"
                            disabled={!selectedFile || uploading}
                            className="w-full px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 disabled:opacity-50 flex items-center justify-center gap-2"
                          >
                            {uploading ? (
                              <><Loader className="w-4 h-4 animate-spin" /> Uploading...</>
                            ) : (
                              <><Upload className="w-4 h-4" /> Upload File</>
                            )}
                          </button>
                        </form>
                      )}

                      {/* Text Evidence Tab */}
                      {activeTab === 'text' && (
                        <form onSubmit={handleTextSubmit} className="space-y-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                            <select
                              value={textType}
                              onChange={(e) => setTextType(e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                            >
                              {EVIDENCE_TYPES.map((t) => (
                                <option key={t.value} value={t.value}>{t.label}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                            <input
                              type="text"
                              value={textTitle}
                              onChange={(e) => setTextTitle(e.target.value)}
                              placeholder="Brief description of this evidence"
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Content</label>
                            <textarea
                              value={textContent}
                              onChange={(e) => setTextContent(e.target.value)}
                              placeholder="Paste configuration, terminal output, or notes here..."
                              rows={6}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                            />
                          </div>
                          <button
                            type="submit"
                            disabled={!textContent.trim() || uploading}
                            className="w-full px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 disabled:opacity-50 flex items-center justify-center gap-2"
                          >
                            {uploading ? (
                              <><Loader className="w-4 h-4 animate-spin" /> Saving...</>
                            ) : (
                              <><FileText className="w-4 h-4" /> Save Evidence</>
                            )}
                          </button>
                        </form>
                      )}

                      {/* Error - only show for non-structured form errors */}
                      {error && !structuredSchema && (
                        <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600 flex items-start gap-2">
                          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                          {error}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer Actions */}
        {!loading && !isCompleted && (
          <div className="border-t border-gray-200 p-4 bg-gray-50">
            {showNAForm ? (
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Justification <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={justification}
                    onChange={(e) => setJustification(e.target.value)}
                    placeholder="Explain why this task is not applicable..."
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => { setShowNAForm(false); setJustification(''); setError(''); }}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleMarkNA}
                    disabled={!justification.trim() || updating}
                    className="flex-1 px-4 py-2 bg-gray-600 text-white rounded-lg text-sm font-medium hover:bg-gray-700 disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {updating ? <Loader className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />}
                    Confirm N/A
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex gap-2">
                {/* Only show Mark as Complete for non-structured-evidence tasks */}
                {!structuredSchema && (
                  <button
                    onClick={handleMarkComplete}
                    disabled={evidenceCount === 0 || updating}
                    title={evidenceCount === 0 ? 'Add at least one piece of evidence first' : 'Mark task as complete'}
                    className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {updating ? <Loader className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                    Mark as Complete
                  </button>
                )}
                <button
                  onClick={() => setShowNAForm(true)}
                  disabled={updating}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  <XCircle className="w-4 h-4" />
                  N/A
                </button>
              </div>
            )}
            {error && !showNAForm && (
              <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-600">
                {error}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default TaskDetailPanel;
